import "./Template.scss";
import * as React from "react";
import { DocumentedComponent } from "../../../shared/components/DocumentedComponent";
import JsxParser from "@recursive-robot/react-jsx-parser";
import styled from "@emotion/styled";
import { useCanvasBindings } from "../../../shared/providers/CanvasBindingsProvider";
import { useJsxParserComponents } from "../../../shared/providers/JsxParserComponentsProvider";
import clsx from "clsx";
import { useSelector } from "react-redux";
import type { RootState } from "../../../store";
import { sendTemplateBindingsToVsCode } from "../../../store/vscode/actions";
import { useTemplateDiagnostics } from "../../../shared/hooks/useTemplateDiagnostics";
import { useProfilingSink } from "../../../shared/hooks/useProfilingSink";

const blacklistedAttrs = [];
const blacklistedTags = [];
const renderError = ({ error }) => <span className="error-message">{`JSX error: ${error}`}</span>;

export type TemplateProps = {
    // Pass in the path to the template object in the storyline...
    path?: string;
    // OR pass in the JSX and CSS directly...
    jsx?: string;
    css?: string;
    // Addtional props are used as (scoped) bindings for the template...
    [key: string]: any;
};

const Template: React.FC<TemplateProps> = (props) => {
    const { path, jsx, css, children, className, ...scopedInputs } = props;
    const _bindings = useCanvasBindings();
    const components = useJsxParserComponents();
    const storeTemplate = useSelector((state: RootState) => path ? state.storyline.storyline.templates[path] : undefined);
    // Stable fallback for inline templates - a fresh object here would force a re-render on every store dispatch...
    const inlineTemplate = React.useMemo<{ jsx?: string, css?: string, version?: number }>(() => ({ jsx, css }), [jsx, css]);
    const template = storeTemplate ?? inlineTemplate;
    const onError = useTemplateDiagnostics(path, template.jsx);
    const { onProfile, enabled: profilingEnabled, reactRenderEnabled } = useProfilingSink();

    const bindings = { ..._bindings, ...scopedInputs, children };

    // Scopes the template's authored CSS under an emotion-generated class on the fragment div (selectors become descendant-scoped)...
    const ScopedRoot = React.useMemo(() => template.css ? styled.div(template.css) : null, [template.css]);

    const result = (
        <JsxParser
            bindings={bindings}
            components={components}
            jsx={template.jsx}
            fileName={path}
            blacklistedTags={blacklistedTags}
            blacklistedAttrs={blacklistedAttrs}
            renderError={renderError}
            onError={onError}
            onProfile={profilingEnabled ? onProfile : undefined}
            profileReactRender={profilingEnabled && reactRenderEnabled}
            renderInWrapper={false}
            key={`${path}:${template.version ?? 0}`}
        />
    );

    // Send the bindings to the VS Code extension, to provide Intellisense for scoped input expressions...
    React.useEffect(() => {
        sendTemplateBindingsToVsCode(path, scopedInputs);
    });

    return ScopedRoot ?
        <ScopedRoot className={clsx(className, "template-fragment")}>{result}</ScopedRoot> :
        result;
};

(Template as DocumentedComponent).metadata = {
    description: "The `Template` component parses and renders the contents of the referenced `jsx` file.  Any additional props provided will be made available to the bindings within this template.  If content is provided between the opening and closing tags of this component, this will be made available to the template via the `children` binding.",
    attributes: [
        { name: "path", type: "string", description: "The relative path to the template file to render.  `{path}.jsx`, `{path}.css` and `{path}.json` are used for the JSX, CSS and documentation respectively." },
        { name: "className", type: "string", description: "The (optional) class name(s) to apply to the container div." }
    ],
    isSelfClosing: true // Even though the Template component can take in children (to act as a wrapper), this is probably the exception rather than the rule...
};

export { Template };